<template>
  <div class="flex-full map-and-list">
    <div class="flex-v selections-geofence-list-container">
      <div class="header">
        {{ capitalize($t('names.placeFound', [geofences.length], geofences.length)) }}
        <div class="checkbox">
          <button
            id="toggle-all-button"
            v-tooltip="
              areAllGeofencesSelected ? capitalize($t('actions.deleteAll')) : capitalize($t('actions.restoreAll'))
            "
            class="btn btn-xs b btn-no-text"
            :class="{ 'btn-danger': areAllGeofencesSelected }"
            @click.stop.prevent="toggleAllGeofencesSelection">
            <icon-delete v-if="areAllGeofencesSelected" />
            <icon-restore v-else />
          </button>
        </div>
      </div>
      <geofence-list
        :active-geofences="selectedGeofences"
        :disabled-geofences="unselectedGeofences"
        :active-geofence="activeGeofence"
        :is-editable="true"
        :show-exclude-checkbox="true"
        @geofence-clicked="activateGeofenceFromList"
        @select-geofence="selectGeofence"
        @unselect-geofence="unselectGeofence" />
    </div>
    <div class="selector-map-container">
      <geofences-list-mapbox
        v-if="isNotChina"
        ref="mapboxMap"
        :geofences="geofences"
        :requested-for-utc="requestedForUtc"
        @unit-clusters-changed="unitClustersChanged"
        @on-active-units-change="onActiveUnitsChange" />
      <geofences-list-baidu-map
        v-else
        ref="baiduMap"
        class="map"
        :enable-map-style-control="true"
        :geofences="geofences"
        :active-geofence="activeGeofence"
        @point-clicked="(geofence) => (activeGeofence = geofence)" />

      <div
        v-if="activeUnits?.length"
        class="unit-details-card">
        <UnitDetailsCard
          :active-units="activeUnits"
          :hide-behind-paywall="true" />
      </div>
    </div>
  </div>
</template>

<script>
  import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

  import { DateTime } from 'luxon';
  import { capitalize } from 'vue';

  import UnitDetailsCard from '@/common/components/map/UnitDetailsCard.vue';
  import { IconDelete, IconRestore } from '@/common/icons/monochrome';
  import { region, Regions } from '@/globals';

  import GeofenceList from './GeofenceList.vue';
  import GeofencesListBaiduMap from './GeofenceListBaiduMap';
  import GeofencesListMapbox from './GeofenceListMapbox';

  export default {
    name: 'GeofenceListMapSelector',
    components: {
      UnitDetailsCard,
      GeofencesListBaiduMap,
      GeofencesListMapbox,
      GeofenceList,
      IconDelete,
      IconRestore,
    },
    props: {
      geofences: {
        type: Array,
      },
      requestedForUtc: {
        type: DateTime,
      },
    },
    data() {
      return {
        activeGeofence: null,
        activeUnits: [],
        clusterRadius: 40,
        geofencesUpdatedTimer: null,
        mapNavigationControl: null,
        mapFullscreenControl: null,
        mapStyle: 'mapbox://styles/mapbox/light-v9',
      };
    },
    computed: {
      selectedGeofences() {
        return this.geofences !== null ? this.geofences.filter((x) => x.isSelected) : [];
      },
      unselectedGeofences() {
        return this.geofences !== null ? this.geofences.filter((x) => !x.isSelected) : [];
      },
      areAllGeofencesSelected() {
        return this.selectedGeofences.length > 0 && this.selectedGeofences.length === this.geofences.length;
      },
      map() {
        return this.isNotChina ? this.$refs.mapboxMap : this.$refs.baiduMap;
      },
      isNotChina() {
        return region !== Regions.China;
      },
    },
    watch: {
      geofences() {
        if (this.geofencesUpdatedTimer != null) {
          clearTimeout(this.geofencesUpdatedTimer);
        }
        this.geofencesUpdatedTimer = setTimeout(() => {
          this.geofencesUpdatedTimer = null;
          this.map.updateGeofenceDataSources();
          this.map.fitAllGeofencesIntoView();
        }, 100);
      },
    },
    mounted() {
      window.addEventListener('resize', () => {
        this.updateGeofenceListRecycleScrollerHeight();
      });
      setTimeout(() => {
        this.updateGeofenceListRecycleScrollerHeight();
      }, 1000);
    },
    methods: {
      capitalize,
      updateGeofenceListRecycleScrollerHeight() {
        const container = document.getElementById('geofence_selection_list_container');
        if (container == null) {
          return;
        }
        const style = `height: ${container.clientHeight}px;`;
        if (document.getElementById('geofence_selection_list_recycle_scroller') != null) {
          document.getElementById('geofence_selection_list_recycle_scroller').style = style;
        }
      },
      activateGeofenceFromList(g, zoomToGeofence = true) {
        this.activeGeofence = g;
        if (zoomToGeofence && this.isNotChina) {
          this.map.zoomToGeofence(this.activeGeofence, false);
        }
      },
      toggleAllGeofencesSelection() {
        if (this.areAllGeofencesSelected) {
          this.geofences.forEach(this.unselectGeofence);
        } else {
          this.geofences.forEach(this.selectGeofence);
        }
        this.map.updateGeofencePolygonDataSource();
        this.map.updateGeofencePointDataSource();
      },
      selectGeofence(g) {
        this.$emit('selectGeofence', g);
      },
      unselectGeofence(g) {
        this.$emit('unselectGeofence', g);
      },
      refresh() {
        if (this.geofencesUpdatedTimer != null) {
          clearTimeout(this.geofencesUpdatedTimer);
        }
        this.geofencesUpdatedTimer = setTimeout(() => {
          this.geofencesUpdatedTimer = null;
          this.map.updateGeofenceDataSources();
          this.map.fitAllGeofencesIntoView();
        }, 100);
      },
      unitClustersChanged(unitClusters) {
        this.$emit('unitClustersChanged', unitClusters);
      },
      onActiveUnitsChange(units) {
        this.activeUnits = units;
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  #app {
    table {
      tbody {
        tr {
          td {
            cursor: pointer;
          }
        }
      }
    }

    #geofences-list-and-map {
      .selections-geofence-list-container {
        flex-basis: 33rem;

        .header {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          justify-content: space-between;
        }

        #toggle-all-button {
          margin-right: 0.8rem;
        }

        .geofence-list {
          font-size: 0.9em;
          //padding-left: 0;
          //padding-top: 0;

          .geofence-list-item-container {
            border-bottom: solid 0.1rem $borders-light;
            padding: 0;
            cursor: pointer;

            &.active {
              background: var(--primaryColor);
              color: var(--primaryColorReverse);
            }
          }

          .geofence-list-item {
            display: flex;
            align-items: center;

            .checkbox {
              margin-right: 0.6rem;
              padding: 0;
            }

            .text {
              flex-grow: 1;
              padding-right: 0.8rem;
              max-width: 22.8rem;
              display: flex;
              flex-direction: column;

              strong {
                display: inline-block;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                line-height: 1.5rem;
                max-width: 20.1rem;
              }

              .address {
                font-size: 0.8em;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
                display: block;
                max-width: 20.1rem;
              }
            }

            .actions {
              flex-basis: 3.2rem;
              flex-shrink: 0;
            }

            .multiselect {
              display: inline-block;
              max-width: 24rem;
              margin-left: -0.6rem;
              margin-top: 1rem;

              .multiselect__tags {
                border-color: var(--actionsColor);

                width: 3rem;
                min-height: 2.4rem;
                padding: 0 3rem 0 0.7rem;
                border-radius: 0;
                position: relative;
                top: -0.4rem;
                cursor: pointer;

                .multiselect__single {
                  color: var(--actionsColor);

                  background: transparent;
                  font-weight: 900;
                }

                &:hover {
                  background: var(--actionsColor);
                  color: var(--actionsColorReverse);

                  .multiselect__single {
                    color: var(--actionsColorReverse);
                  }
                }
              }

              .multiselect__select {
                display: none;
              }

              .multiselect__content-wrapper {
                max-height: 30rem;
                width: 20rem;
                position: fixed;

                .multiselect__content {
                  box-shadow: var(--panesShadow);

                  max-height: initial !important;
                  border-color: $borders;
                  width: auto;
                  font-weight: 400;
                  border-radius: 0;
                  border: 0.1rem solid $borders;

                  .multiselect__element {
                    font-size: 0.9em;

                    span {
                      margin: 0;
                      padding: 0.8rem 1.2rem;
                      min-height: 0;
                    }
                  }

                  .multiselect__option {
                    min-width: 10rem;
                    border-top: 0.1rem solid $borders-light;
                    cursor: pointer;

                    svg {
                      stroke: var(--textColor);

                      width: 1.6rem;
                      height: 1.6rem;
                      display: inline-block;
                      margin-right: 0.5rem;
                    }

                    .option__desc {
                      display: inline-block;
                      padding-right: 0.3rem;
                      position: relative;
                      top: -0.2rem;
                    }
                  }

                  .multiselect__option--highlight {
                    svg {
                      stroke: #fff;
                    }
                  }
                }
              }
            }
          }
        }
      }

      .selector-map-container {
        flex-grow: 1;
        margin: 0;
        display: flex;
        overflow: hidden;
        position: relative;

        .map {
          flex-grow: 1;
          margin: 0;
          width: 100%;
          height: 100%;
        }
      }

      .map-and-list {
        border: solid 0.1rem $borders;
        margin: 1.6rem 0;
        overflow: hidden;

        .selections-geofence-list-container {
          > div:first-of-type {
            padding: 0.8rem 1.2rem;
            border-bottom: solid 0.1rem $borders;
          }
        }
      }
    }

    .unit-details-card {
      position: absolute;
      top: 1rem;
      left: 1rem;
      flex-grow: 1;
      margin: 0;
      height: 100%;
      width: 30%;
    }
  }
</style>
